import { scheduleDisplay, scheduleInstant, legacyBoundary } from '../../schedule';
import RestoredScheduleNotice from '../RestoredScheduleNotice';
import { needsClaimBudgetReview } from '../../budgetEstimate';
import ImageRecommendationSession from '../OfferDetails/components/ImageUpload/ImageRecommendationSession';
import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { useRef } from 'react';
import moment from "moment";
import isEqual from "lodash/isEqual";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  styled,
  DialogActions,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import Stepper from "../Stepper";
import OfferAudience from "../OfferAudience";
import OfferBudget from "../OfferBudget";
import OfferDescription from "../OfferDescription";
import OfferCode from "../OfferCode";
import OfferDetails from "../OfferDetails";
import OfferReview from "../OfferReview";
import OfferPayment from "../OfferPayment";
import OfferFund from "../OfferFund";
import api from "utils/api";
import { getApiErrorMessage } from "utils/apiErrors";
import { canEditOffer, offerSaveImpact, normalizeOfferStatus } from "utils/offerPermissions";
import { getOfferFulfillmentDefault, mapOfferToForm } from "./utils";
import {
  draftCurrentStep,
  draftOfferDocumentToForm,
  formToDraftOfferDocument,
  reviewDraftOfferDocument,
} from "../../draftOfferApi";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { stripeApiKey } from "utils/config";
import { hasMinimumLaunchBalance, parseAccountBalance } from "utils/businessBalance";
import CloseIcon from '@mui/icons-material/DisabledByDefault';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

const CustomDialog = styled(Dialog)({
  "& .MuiDialog-scrollPaper": {
    alignItems: "flex-start",
  },
});

const AUTO_SAVE_DELAY = 1000; // 1 second debounce
const HEADLINE_1_MAX_LENGTH = 30;
const REVIEW_STEP_INDEX = 5;
const ALL_REVIEW_STEPS = [0, 1, 2, 3, 4, REVIEW_STEP_INDEX];

const REVIEW_STEP_FIELDS = [
  new Set(['offer', 'businessId', 'title', 'fulfillment', 'categoryIds', 'keywords']),
  new Set(['location']),
  new Set(['headline1', 'headline2', 'description', 'siteUrl', 'termsAndConditions', 'offerImage']),
  new Set(['redemption']),
  new Set(['budget', 'claimPolicy', 'startDate', 'endDate']),
];

const issueStep = (issue) => {
  const step = REVIEW_STEP_FIELDS.findIndex((fields) => fields.has(issue?.field));
  return step < 0 ? REVIEW_STEP_INDEX : step;
};

const OfferWizard = ({
  offer,
  isDraft,
  onClose,
  onOfferDataRefresh,
  onOfferUpdated,
}) => {
  const businessCategories = useSelector((state) => state.business.categories);
  const businessKeywords = useSelector((state) => state.business.keywords);
  const businessId = useSelector((state) => state.business.businessId);
  const { enqueueSnackbar } = useSnackbar();
  //const [activeStep, setActiveStep] = useState(offer ? 7 : 0);
  //const [activeStep, setActiveStep] = useState(offer ? 0 : 0);
  const business = useSelector((state) => state.business);
  const defaultFulfillment = getOfferFulfillmentDefault(
    business.companyInfo?.fulfillment
  );
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const restoredDraft = Boolean(isDraft && offer?.draftId);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [reviewIssues, setReviewIssues] = useState([]);
  const [visibleReviewSteps, setVisibleReviewSteps] = useState(() =>
    isDraft && offer?.draftId ? ALL_REVIEW_STEPS : []
  );
  const [serverReviewReady, setServerReviewReady] = useState(false);
  const initialReviewRef = useRef(false);

  const dialogContentRef = useRef(null);

useEffect(() => {
  if (isDraft && offer?.draftId && !draftId) {
    setDraftId(offer.draftId);
  }
}, [isDraft, offer, draftId]);

const [values, setValues] = useState(() => {
  if (isDraft && offer) {
    const draftValues = draftOfferDocumentToForm(offer, businessCategories);
    return {
      ...draftValues,
      description: {
        ...draftValues.description,
        fulfillment:
          getOfferFulfillmentDefault(
            draftValues.description?.fulfillment
          ) || defaultFulfillment,
      },
      currentStep: draftCurrentStep(offer),
    };
  } else if (offer?.offerDetails) {
    // For offer editing - use mapped offer data
    return mapOfferToForm(
      offer.offerDetails,
      business.companyInfo?.fulfillment
    );
  }
  // New offer/draft - use business defaults
  return {
    description: {
      fulfillment: defaultFulfillment,
      category: businessCategories,
      keywords: businessKeywords,
    },
    details: {
      headline1: String(business.companyInfo?.companyName || '')
        .slice(0, HEADLINE_1_MAX_LENGTH),
    },
    currentStep: 0
  };
});
const [claimReviewRequired, setClaimReviewRequired] = useState(false);
const confirmedBudgetValuesRef = useRef(isDraft && draftCurrentStep(offer) >= REVIEW_STEP_INDEX ? values : null);
const pendingValuesRef = useRef({});
const dirtySectionsRef = useRef({});
const cleanValuesRef = useRef(values);

// Set initial active step based on draft/offer.
const [activeStep, setActiveStep] = useState(
  isDraft && offer && (
    getOfferFulfillmentDefault(offer.payload?.offer?.fulfillment) ||
    defaultFulfillment
  )
    ? draftCurrentStep(offer)
    : 0
);

const getCurrentEditorValues = useCallback(() => ({
  ...values,
  ...pendingValuesRef.current,
}), [values]);

const reviewValues = useCallback(async (candidateValues) => {
  if (!isOnline) {
    setServerReviewReady(false);
    return { values: candidateValues, issues: null, readyForReview: false };
  }
  const document = formToDraftOfferDocument({
    values: candidateValues,
    businessId,
    currentStep: activeStep,
    existingDraft: isDraft && offer ? offer : undefined,
  });
  const [review, needsClaimReview] = await Promise.all([
    reviewDraftOfferDocument(document),
    needsClaimBudgetReview(businessId, candidateValues, confirmedBudgetValuesRef.current),
  ]);
  setClaimReviewRequired(needsClaimReview);
  const issues = Array.isArray(review?.issues) ? review.issues : [];
  setReviewIssues(issues);
  setServerReviewReady(Boolean(review?.readyForReview) && !needsClaimReview);
  return { values: candidateValues, issues, readyForReview: Boolean(review?.readyForReview), needsClaimReview };
}, [activeStep, businessId, isDraft, isOnline, offer]);

const completeStep = useCallback(async (section, sectionValues, nextStep, requireFullReview = false) => {
  const candidateValues = {
    ...getCurrentEditorValues(),
    [section]: sectionValues,
  };
  if (offer?.offerDetails && !isDraft) {
    setValues(candidateValues);
    setActiveStep(nextStep);
    return;
  }
  try {
    const review = await reviewValues(candidateValues);
    setVisibleReviewSteps((steps) => requireFullReview
      ? ALL_REVIEW_STEPS
      : steps.includes(activeStep) ? steps : [...steps, activeStep]);
    setValues(review.values);
    pendingValuesRef.current = {};
    if (review.issues === null) {
      setActiveStep(nextStep);
      return;
    }
    const blocking = requireFullReview
      ? review.issues
      : review.issues.filter((issue) => issueStep(issue) === activeStep);
    if (blocking.length > 0) {
      setActiveStep(Math.min(...blocking.map(issueStep)));
      return;
    }
    if (section === 'budget') {
      confirmedBudgetValuesRef.current = review.values;
      setClaimReviewRequired(false);
      setServerReviewReady(review.readyForReview);
    }
    setActiveStep(nextStep);
  } catch (error) {
    setServerReviewReady(false);
    enqueueSnackbar(getApiErrorMessage(error, 'Couldn’t review this offer. Please try again.'), {
      variant: 'error',
    });
  }
}, [activeStep, enqueueSnackbar, getCurrentEditorValues, isDraft, offer, reviewValues]);

const handleStepValuesChange = useCallback((section, sectionValues, isDirty) => {
  const hasUnsavedSectionChanges = Boolean(
    isDirty && !isEqual(sectionValues, cleanValuesRef.current?.[section])
  );

  dirtySectionsRef.current = {
    ...dirtySectionsRef.current,
    [section]: hasUnsavedSectionChanges,
  };

  if (hasUnsavedSectionChanges) {
    if (!offer || isDraft) {
      setServerReviewReady(false);
    }
    pendingValuesRef.current = {
      ...pendingValuesRef.current,
      [section]: sectionValues,
    };
  } else {
    const nextPendingValues = { ...pendingValuesRef.current };
    delete nextPendingValues[section];
    pendingValuesRef.current = nextPendingValues;
  }
}, [isDraft, offer]);

const autoSaveStepRef = useRef(activeStep);

useEffect(() => {
  if (dialogContentRef.current) {
    dialogContentRef.current.scrollTop = 0;
  }
}, [activeStep]);

useEffect(() => {
  if (!isDraft || !offer || initialReviewRef.current || !isOnline) return;
  initialReviewRef.current = true;
  reviewValues(getCurrentEditorValues())
    .then((review) => {
      setValues(review.values);
      pendingValuesRef.current = {};
      if (review.issues?.length) {
        setActiveStep(Math.min(...review.issues.map(issueStep)));
      }
    })
    .catch((error) => {
      setServerReviewReady(false);
      enqueueSnackbar(getApiErrorMessage(error, 'Couldn’t review this draft.'), {
        variant: 'error',
      });
    });
}, [enqueueSnackbar, getCurrentEditorValues, isDraft, isOnline, offer, reviewValues]);

useEffect(() => {
  const handleOnline = () => setIsOnline(true);
  const handleOffline = () => setIsOnline(false);

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}, []);

  const stripePromise = loadStripe(stripeApiKey);
  

  const balance = parseAccountBalance(business.accountBalance);
  const additionalSteps = hasMinimumLaunchBalance(balance, business)
    ? []
    : ["Payment", "Fund"];
  const isEdit = !!offer;
  const isDraftFlag = !!offer?.draftId;
  const isEditingActualOffer = isEdit && !isDraftFlag;
  const sourceStatus = offer?.offerDetails?.status || offer?.status;
  const [actualStatus, setActualStatus] = useState(sourceStatus);
  useEffect(() => { setActualStatus(sourceStatus); }, [sourceStatus]);
  useEffect(() => {
    if (isEditingActualOffer && !canEditOffer(actualStatus)) {
      enqueueSnackbar('This offer cannot be edited in its current state. Refresh the offer list and use an available action.', { variant: 'warning' });
      onClose();
    }
  }, [actualStatus, isEditingActualOffer, enqueueSnackbar, onClose]);
  const isDraftLaunch = !offer || isDraftFlag;
  const saveButtonLabel = isEditingActualOffer ? "Save a copy as draft" : "Save draft";

  const saveDraftLocally = useCallback((overrideValues) => {
    const currentValues = getCurrentEditorValues();
    const dataToSave = overrideValues ? { ...currentValues, ...overrideValues } : currentValues;
    localStorage.setItem(`draft-offer-${businessId}`, JSON.stringify({
      ...dataToSave,
      currentStep: claimReviewRequired ? Math.min(activeStep, 4) : activeStep,
    }));
  }, [activeStep, businessId, claimReviewRequired, getCurrentEditorValues]);

  const saveDraft = useCallback(async (
    overrideValues,
    { refreshDraftData = true } = {}
  ) => {
    if (!isOnline) {
      saveDraftLocally(overrideValues);
      return { draftId: null, created: false };
    }

    setIsSaving(true);
    try {
      const currentValues = getCurrentEditorValues();
      const dataToSave = overrideValues ? { ...currentValues, ...overrideValues } : currentValues;
      const needsClaimReview = !offer?.offerDetails && await needsClaimBudgetReview(
        businessId, dataToSave, confirmedBudgetValuesRef.current
      );
      const payload = formToDraftOfferDocument({
        values: dataToSave,
        businessId,
        currentStep: needsClaimReview ? Math.min(activeStep, 4) : activeStep,
        existingDraft: isDraft && offer ? offer : undefined,
      });

      let savedDraftId;
      let created = false;
      if (draftId) {
        await api.put(`/drafts/${draftId}`, payload);
        savedDraftId = draftId;
      } else {
        const response = await api.post("/drafts", payload);
        savedDraftId = response.draftId;
        created = true;
        setDraftId(savedDraftId);
      }

      if (refreshDraftData) {
        await onOfferDataRefresh?.(['DRAFT']);
      }

      // Clear local storage if online save succeeds
      localStorage.removeItem(`draft-offer-${businessId}`);
      return { draftId: savedDraftId, created };
    } catch (error) {
      console.error("Draft save failed:", error);
      saveDraftLocally(overrideValues);
      throw error;
    } finally {
       setIsSaving(false);
      setIsSaved(true); 
      setTimeout(() => setIsSaved(false), 2000);
    }
  }, [
    activeStep,
    businessId,
    draftId,
    getCurrentEditorValues,
    isOnline,
    isDraft,
    offer,
    onOfferDataRefresh,
    saveDraftLocally,
  ]);

  // Keep Next non-blocking while persisting progress for new and existing drafts.
  useEffect(() => {
    const previousStep = autoSaveStepRef.current;
    autoSaveStepRef.current = activeStep;
    if (activeStep <= previousStep) return;
    if (offer && !isDraft) return;

    const timer = setTimeout(() => {
      if (values && Object.keys(values).length > 0 && activeStep > 0) {
        saveDraft().catch(() => {});
      }
    }, AUTO_SAVE_DELAY);

    return () => clearTimeout(timer);
  }, [activeStep, isDraft, offer, saveDraft, values]);

  const buildOfferPayload = (offerValues) => ({
    ...offerValues.description,
    ...offerValues.details,
    ...offerValues.code,
    offerLocation: offerValues.audience,
    budget: offerValues.budget,
    businessId,
    startDate: moment(offerValues.budget.startDate).toISOString(),
    endDate: moment(offerValues.budget.endDate).toISOString(),
  });

  const updateActualOffer = async (overrideValues = {}, closeAfterUpdate = false) => {
    if (!canEditOffer(actualStatus)) {
      enqueueSnackbar('This offer cannot be edited in its current state.', { variant: 'warning' });
      return;
    }
    const updatedValues = {
      ...getCurrentEditorValues(),
      ...overrideValues,
    };
    const offerId = offer?.offerDetails?.offerId || offer?.offerId;

    if (!offerId) {
      enqueueSnackbar("Offer not found.", { variant: "error" });
      return;
    }

    if (!getOfferFulfillmentDefault(updatedValues.description?.fulfillment)) {
      enqueueSnackbar('Select a fulfillment type before updating this offer.', {
        variant: 'error',
      });
      setActiveStep(0);
      return;
    }

    try {
      setIsSaving(true);
      const response = await api.patch(`/offer/${offerId}`, buildOfferPayload(updatedValues));
      setActualStatus(response.status);
      setValues(updatedValues);
      pendingValuesRef.current = {};
      dirtySectionsRef.current = {};
      cleanValuesRef.current = updatedValues;
      await onOfferUpdated?.(response);
      const savedStatus = normalizeOfferStatus(response.status).toLowerCase();
      enqueueSnackbar(`Offer updated. Status: ${savedStatus}.${savedStatus === 'paused' ? ' Activate it separately when you are ready.' : ''}`, { variant: "success" });
      setShowSaveConfirm(false);
      if (closeAfterUpdate) onClose();
    } catch (error) {
      console.error("Offer update failed:", error);
      if (error?.code !== 'IMAGE_NOT_FOUND') {
        try {
          const latest = await api.get(`/offer/${offerId}`);
          if (latest?.status) setActualStatus(latest.status);
          await onOfferUpdated?.({ ...latest, status: normalizeOfferStatus(latest?.status) });
        } catch (_) { /* Keep the save error visible if refreshing also fails. */ }
      }
      enqueueSnackbar(getApiErrorMessage(error, "Couldn’t update offer."), {
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    const isDraftLaunch = !offer || !!offer?.draftId;
    let createdDraftDuringLaunch = false;

    try {
      let launchValues = getCurrentEditorValues();
      if (isDraftLaunch) {
        if (!isOnline) {
          enqueueSnackbar('Reconnect before reviewing or launching this draft.', {
            variant: 'warning',
          });
          return;
        }
        const review = await reviewValues(launchValues);
        setVisibleReviewSteps(ALL_REVIEW_STEPS);
        launchValues = review.values;
        setValues(launchValues);
        pendingValuesRef.current = {};
        if (review.needsClaimReview) {
          setActiveStep(4);
          return;
        }
        if (!review.readyForReview || review.issues?.length) {
          if (review.issues?.length) {
            setActiveStep(Math.min(...review.issues.map(issueStep)));
          }
          return;
        }
      }

      if (!getOfferFulfillmentDefault(launchValues.description?.fulfillment)) {
        enqueueSnackbar('Select a fulfillment type before launching this offer.', {
          variant: 'error',
        });
        setActiveStep(0);
        return;
      }

      let response;
      const isDraft = !!offer?.draftId;

      // Launching a new offer or a saved draft uses the backend draft mapper.
      if (!offer || isDraft) {
        const savedDraft = await saveDraft(launchValues, { refreshDraftData: false });
        createdDraftDuringLaunch = savedDraft.created;
        if (!savedDraft.draftId) {
          throw new Error("Draft must be persisted before launch.");
        }
        response = await api.post(`/drafts/${savedDraft.draftId}/launch`);
      } else {
        await updateActualOffer({}, true);
        return;
      }

      let statusMessage;
      switch (response.status) {
        case "EXPIRED":
          statusMessage = "Offer expired.";
          break;
        case "SCHEDULED":
          statusMessage = `Offer scheduled on ${scheduleDisplay(
            response.effectiveStartAt
              || legacyBoundary(response.effectiveStartDate)
              || launchValues.budget?.startDate
          )}`;
          break;
        case "PAUSED":
          statusMessage = response.statusReason
            ? `Offer paused: ${response.statusReason}`
            : "Offer paused.";
          break;
        case "ACTIVE":
          statusMessage = "Offer launched.";
          break;
        default:
          throw new Error("Unexpected status: " + response.status);
      }

      enqueueSnackbar(statusMessage, { variant: "success" });

      await onOfferDataRefresh?.(['DRAFT', response.status]);
      onClose();
    } catch (error) {
      console.error("Offer submission error:", error);
      if (createdDraftDuringLaunch) {
        await onOfferDataRefresh?.(['DRAFT']);
      }
      const fallback = isDraftLaunch ? "Couldn’t launch offer." : "Couldn’t update offer.";
      enqueueSnackbar(getApiErrorMessage(error, fallback), { variant: "error" });
    }
  };

  const handleCloseClick = () => {
    const hasUnsavedChanges = Object.values(dirtySectionsRef.current).some(Boolean);

    if (hasUnsavedChanges) {
      setShowSaveConfirm(true);
    } else {
      onClose();
    }
  };

  const isEventLike = (arg) => !!arg && (arg.nativeEvent || arg.target || typeof arg.preventDefault === 'function');

  const handleSaveOffer = async (maybeOverrideValues, closeAfterSave = false) => {
    const overrideValues = isEventLike(maybeOverrideValues) ? undefined : maybeOverrideValues;
    try {
      await saveDraft(overrideValues);
      if (!isEditingActualOffer) {
        const savedValues = {
          ...getCurrentEditorValues(),
          ...overrideValues,
        };
        setValues(savedValues);
        pendingValuesRef.current = {};
        dirtySectionsRef.current = {};
        cleanValuesRef.current = savedValues;
      }
      setShowSaveConfirm(false);
      if (closeAfterSave || !isEditingActualOffer) onClose();
      enqueueSnackbar(
        isEditingActualOffer ? "Draft copy saved." : "Draft saved.",
        { variant: "success" }
      );
    } catch (error) {
      enqueueSnackbar(
        getApiErrorMessage(
          error,
          isEditingActualOffer ? "Couldn’t save draft copy." : "Couldn’t save draft."
        ),
        { variant: "error" }
      );
    }
  };

  const handleDiscardOffer = () => {
    localStorage.removeItem(`draft-offer-${businessId}`);
    setShowSaveConfirm(false);
    onClose();
  };

  return (
    <>
      <ImageRecommendationSession sessionKey={`${businessId}:${isDraft ? 'draft' : 'offer'}:${offer?.offerDetails?.offerId || offer?.offerId || offer?.draftId || 'new'}`}>
      <CustomDialog fullWidth maxWidth="md" open={true}>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box mb={1}>Configure offer</Box>
            <Box display="flex" alignItems="center">
              {isSaving && (
                <Box display="flex" alignItems="center" mr={2}>
                  <CircularProgress size={20} />
                  <Typography variant="caption" ml={1}>Saving...</Typography>
                </Box>
              )}

              {!isSaving && isSaved && (
                <Box display="flex" alignItems="center" mr={2}>
                  <CheckCircleIcon fontSize="small" sx={{ color: 'green' }} />
                  <Typography variant="caption" ml={1} sx={{ color: 'green' }}>
                    Saved
                  </Typography>
                </Box>
              )}
              {/* {!isOnline && (
                <Typography variant="caption" color="error" mr={2}>
                  Offline - saving locally
                </Typography>
              )} */}
              <IconButton
                aria-label="Close offer editor"
                onClick={handleCloseClick}
                size="small"
                sx={{ color: "red" }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Stepper
            activeStep={activeStep}
            steps={[
              "Description",
              "Audience",
              "Terms",
              "Coupon",
              "Limits",
              "Review",
              ...additionalSteps
            ]}
            onStepChange={(s) => setActiveStep(s)}
          />
        </DialogTitle>
          <DialogContent
            ref={dialogContentRef}
            sx={{
              maxHeight: '75vh',
              overflowY: 'auto',
              '::-webkit-scrollbar': { width: 10 },
              '::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '5px',
                '&:hover': { background: '#555' },
              },
              '::-webkit-scrollbar-track': { background: '#f0f0f0' },
            }}
          >
          {isEditingActualOffer && (
            <Alert severity="info" sx={{ mb: 2 }}>{offerSaveImpact(actualStatus)}</Alert>
          )}
          {!isOnline && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              You can keep editing and save locally while offline, but reconnect before reviewing or launching this offer.
            </Alert>
          )}
          {claimReviewRequired && activeStep > 4 && !isEditingActualOffer && (
            <Box sx={{ mb: 2 }}>
              <Button onClick={() => setActiveStep(4)}>Review claim limit</Button>
            </Box>
          )}
          {reviewIssues
            .filter((issue) => visibleReviewSteps.includes(activeStep) && issueStep(issue) === activeStep)
            .map((issue) => (
              <Alert key={issue.code} severity="error" sx={{ mb: 1 }}>
                {issue.message}
              </Alert>
            ))}
          {activeStep === 0 && (
            <OfferDescription
              values={values}
              onClose={onClose}
              onComplete={(val) => completeStep('description', val, 1)}
              onSave={handleSaveOffer}
              saveButtonLabel={saveButtonLabel}
              onUpdate={isEditingActualOffer ? updateActualOffer : undefined}
              onValuesChange={handleStepValuesChange}
            />
          )}
          
          {activeStep === 1 && (
            <OfferAudience
              values={values}
              onClose={onClose}
              onComplete={(val) => completeStep('audience', val, 2)}
              back={() => setActiveStep(0)}
              onSave={handleSaveOffer}
              saveButtonLabel={saveButtonLabel}
              onUpdate={isEditingActualOffer ? updateActualOffer : undefined}
              onValuesChange={handleStepValuesChange}
            />
          )}
          
          {activeStep === 2 && (
            <OfferDetails
              preserveLegacyImage={isEditingActualOffer && !String(offer?.offerDetails?.offerImage ?? offer?.offerImage ?? '').trim()}
              values={values}
              onClose={onClose}
              onComplete={(val) => completeStep('details', val, 3)}
              back={() => setActiveStep(1)}
              onSave={handleSaveOffer}
              saveButtonLabel={saveButtonLabel}
              onUpdate={isEditingActualOffer ? updateActualOffer : undefined}
              onValuesChange={handleStepValuesChange}
            />
          )}
          
          {activeStep === 3 && (
            <OfferCode
              values={values}
              onClose={onClose}
              onComplete={(val) => completeStep('code', val, 4)}
              back={() => setActiveStep(2)}
              onSave={handleSaveOffer}
              saveButtonLabel={saveButtonLabel}
              onUpdate={isEditingActualOffer ? updateActualOffer : undefined}
              onValuesChange={handleStepValuesChange}
            />
          )}
          
          {(activeStep === 4 || activeStep === 5) && (
            <RestoredScheduleNotice restored={restoredDraft}
              startAt={scheduleInstant(values.budget?.startDate)}
              endAt={scheduleInstant(values.budget?.endDate)} />
          )}
          {activeStep === 4 && (
            <OfferBudget
              values={values}
              onClose={onClose}
              onComplete={(val) => completeStep('budget', val, 5, true)}
              back={() => setActiveStep(3)}
              onSave={handleSaveOffer}
              onDiscard={handleDiscardOffer}
              saveButtonLabel={saveButtonLabel}
              onUpdate={isEditingActualOffer ? updateActualOffer : undefined}
              onValuesChange={handleStepValuesChange}
            />
          )}
          
          {activeStep === 5 && (
            <OfferReview
              preserveLegacyImage={isEditingActualOffer && !String(offer?.offerDetails?.offerImage ?? offer?.offerImage ?? '').trim()}
              values={values}
              onClose={onClose}
              onComplete={() => {
                if (hasMinimumLaunchBalance(business.accountBalance, business)) {
                  handleSubmit();
                } else {
                  setActiveStep(6);
                }
              }}
              back={() => setActiveStep(4)}
              onSave={handleSaveOffer}
              onUpdate={isEditingActualOffer ? updateActualOffer : undefined}
              saveButtonLabel={saveButtonLabel}
              isEditingActive={isEditingActualOffer}
              resetToStart={() => setActiveStep(0)}
              serverReviewReady={!isDraftLaunch || (serverReviewReady && !claimReviewRequired)}
            />
          )}
          
          {activeStep === 6 && (
            <Elements stripe={stripePromise}>
              <OfferPayment
                values={values}
                onClose={onClose}
                setActiveStep={setActiveStep}
                onSave={handleSaveOffer}
                saveButtonLabel={saveButtonLabel}
                onUpdate={isEditingActualOffer ? updateActualOffer : undefined}
                onComplete={(val) => {
                  const updatedPayment = {
                    ...val,
                    card: val.paymentUpdated?.card,
                  };
                  setValues({ ...values, paymentUpdated: updatedPayment });
                  setActiveStep(7);
                }}
                back={() => setActiveStep(5)}
              />
            </Elements>
          )}
          
          {activeStep === 7 && (
            <OfferFund 
              values={values} 
              business={business} 
              onClose={onClose} 
              onComplete={handleSubmit} 
              back={() => setActiveStep(6)}
              onSave={handleSaveOffer}
              onUpdate={isEditingActualOffer ? updateActualOffer : undefined}
              saveButtonLabel={saveButtonLabel}
              isEditingActive={isEditingActualOffer}
            />
          )}
        </DialogContent>
      </CustomDialog>
      </ImageRecommendationSession>

      {/* Save Confirmation Dialog */}
      <Dialog
        open={showSaveConfirm}
        onClose={() => setShowSaveConfirm(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, pt: 3, pb: 1 }}>
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 36,
              height: 36,
              borderRadius: '50%',
              color: '#9A6200',
              bgcolor: 'rgba(249, 183, 86, 0.2)',
              flexShrink: 0,
            }}
          >
            <WarningAmberRoundedIcon fontSize="small" />
          </Box>
          <Typography component="span" sx={{ fontSize: 20, fontWeight: 600 }}>
            {isEditingActualOffer ? "Save changes?" : "Save draft?"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3, color: 'text.secondary', fontSize: 15 }}>
          {isEditingActualOffer
            ? `Update this offer before closing? ${offerSaveImpact(actualStatus)}`
            : "Save this draft before closing?"}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: 1,
            borderColor: 'divider',
            gap: 0.5,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            '& .MuiButton-root': {
              minHeight: 40,
              width: { xs: '100%', sm: 'auto' },
            },
          }}
        >
          <Button onClick={() => setShowSaveConfirm(false)} sx={{ color: 'text.secondary' }}>
            Keep editing
          </Button>
          <Button onClick={handleDiscardOffer} color="error">
            Discard & close
          </Button>
          <Button
            onClick={() => isEditingActualOffer
              ? updateActualOffer({}, true)
              : handleSaveOffer(undefined, true)}
            color="primary"
            variant="contained"
          >
            {isEditingActualOffer ? "Update & close" : "Save & close"}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default OfferWizard;
